// src/components/jarvisly-components/others/TileItem.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState } from 'react';
import { Button, Card, Divider, Dropdown, Flex, Spin, Tag, Tooltip, theme } from 'antd';
import translateX from 'utils/translateX';
import { getFlag } from 'utils/phone';
import AccountProfileTitle from 'modules/app-views/account/components/AccountProfileTitle';
import { useNavigate } from 'react-router-dom';
import {
  LikeOutlined,
  CloseOutlined,
  StopOutlined,
  SwapOutlined,
  PlusOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import useZustand from 'hooks/useZustand';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';
import SimpleAvatarGroup from 'components/SimpleComponents/SimpleAvatarGroup';
import axiosService from 'services/axiosService';
import authService from 'services/authService';
import appService from 'services/appService';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// -2: blocked, -1: declined, 0: suspended, 1: invited, 2: accepted, 3: expired
const STATUS_CONFIG = {
  '-2': { color: 'default', label: 'blocked', status: 'blocked', bgColor: '#c0c0c00f' },
  '-1': { color: 'default', label: 'declined', status: 'declined', bgColor: '#c0c0c00f' },
  0: { color: 'error', label: 'suspended', status: 'suspended', bgColor: '#ff00000f' },
  1: { color: 'warning', label: 'awaiting_acceptance', status: 'invited', bgColor: '#fff0000f' },
  2: { color: 'success', label: 'active', status: 'accepted', bgColor: '#00ff720f' },
  3: { color: 'default', label: 'expired', status: 'expired', bgColor: '#c0c0c00f' },
};

// Country-origin flag (bottom-right of each tile), same source as SimpleCountry
const FLAG_STYLE = { width: 22, height: 15, borderRadius: 2, objectFit: 'cover', flexShrink: 0 };

// *******************************************************************************
// EXTERNAL COMPONENT
// *******************************************************************************
export const AddTileItem = ({ onClick }) => {
  const { token } = theme.useToken();
  const isDark = token.colorBgContainer !== '#ffffff';

  const bgColor = token.colorBgContainer;
  const hoverBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.035)';
  const hoverShadow = isDark
    ? '0 1px 2px -2px rgba(0,0,0,0.64), 0 3px 6px 0 rgba(0,0,0,0.48), 0 5px 12px 4px rgba(0,0,0,0.36), 0 0 0 1px rgba(255,255,255,0.1)'
    : '0 1px 2px -2px rgba(0,0,0,0.16), 0 3px 6px 0 rgba(0,0,0,0.12), 0 5px 12px 4px rgba(0,0,0,0.09)';

  return (
    <Card
      hoverable={!isDark}
      className="strong-border"
      onClick={onClick}
      styles={{
        body: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 141,
          background: bgColor,
          borderRadius: 10,
          transition: 'background 0.3s ease, box-shadow 0.3s ease',
        },
      }}
      style={{
        cursor: 'pointer',
        transition: 'box-shadow 0.3s ease',
      }}
      onMouseEnter={e => {
        const card = e.currentTarget;
        const body = card.querySelector('.ant-card-body');
        if (body) body.style.background = hoverBg;
        if (isDark) card.style.boxShadow = hoverShadow;
      }}
      onMouseLeave={e => {
        const card = e.currentTarget;
        const body = card.querySelector('.ant-card-body');
        if (body) body.style.background = bgColor;
        if (isDark) card.style.boxShadow = 'none';
      }}
    >
      <PlusOutlined style={{ fontSize: 26, opacity: 0.6 }} />
    </Card>
  );
};

// *******************************************************************************
// INTERNAL COMPONENTS
// *******************************************************************************
const OrganizationAvatar = props => {
  const { organization, logo, backColor = 'blue', foreColor = 'white' } = props;

  return (
    <SimpleAvatar
      src={logo}
      name={organization}
      size={52}
      shape="square"
      backColor={backColor}
      foreColor={foreColor}
      showBorder={false}
      style={{ flexShrink: 0 }}
    />
  );
};

const ItemHeader = props => {
  // props deconstruction ------------------------------------------------------
  const { title, profile } = props;
  const { token } = theme.useToken();

  // 8. UI COMPONENT --------------------------------------------------------------
  return (
    <Flex vertical justify="space-between" style={{ height: '52px', flex: 1 }}>
      <h4 className="mb-0" style={{ lineHeight: '1.3', marginTop: '2px', color: token.colorText }}>
        {title}
      </h4>

      <AccountProfileTitle profile={profile} />
    </Flex>
  );
};

const StatusTag = ({ status }) => {
  const config = STATUS_CONFIG[String(status)];
  if (!config) return null;

  return (
    <Tag color={config.color} variant="solid">
      {translateX(config.label)}
    </Tag>
  );
};

const ItemMember = ({ team }) => {
  if (!team?.length) return null;

  const items = team.map((member, i) => ({
    key: member?._id || i,
    src: getTeamAvatarSrc(member),
    name: member?.name,
    backColor: member?.backColor,
    foreColor: member?.foreColor,
  }));

  return <SimpleAvatarGroup items={items} maxCount={3} size={24} />;
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const TileItem = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { record, hoverable, strongBorder, showAvatar } = props;
  const setPageStore = useZustand('module', 'setPage');
  const storedUser = useZustand('auth', 'user');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const navigate = useNavigate();
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isOwner = record?.profile === 'owner';
  const statusConfig = STATUS_CONFIG[String(record?.status)];
  const cardBgColor = !isOwner && statusConfig?.bgColor ? statusConfig.bgColor : undefined;
  const logo = record?.avatar?.base64Thumb || record?.avatar?.thumbUrl || record?.avatar?.largeUrl;

  // Subscription data-origin flag (countryCode -> SimpleCountry/SubscriptionSelector flag source).
  // Prefer the mainCompany data origin; fall back to subscription country / i18n metadata.
  const countryCode = (
    record?.mainCompany?.countryCode ||
    record?.countryCode ||
    record?._metadata?.i18n?.countryCode ||
    ''
  ).toUpperCase();
  const flagSrc = countryCode ? getFlag(countryCode) : null;

  const isDark = token.colorBgContainer !== '#ffffff';
  const bgColor = cardBgColor || token.colorBgContainer;
  const hoverBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.035)';
  const hoverShadow = isDark
    ? '0 1px 2px -2px rgba(0,0,0,0.64), 0 3px 6px 0 rgba(0,0,0,0.48), 0 5px 12px 4px rgba(0,0,0,0.36), 0 0 0 1px rgba(255,255,255,0.1)'
    : '0 1px 2px -2px rgba(0,0,0,0.16), 0 3px 6px 0 rgba(0,0,0,0.12), 0 5px 12px 4px rgba(0,0,0,0.09)';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const onCardClick = () => {
    if (!isOwner) return;
    if (!storedUser?._id || !record?._id) return;

    setPageStore({
      navigate,
      pageName: 'subscription',
      params: {
        userId: storedUser._id,
        subscriptionId: record._id,
      },
    });
  };

  const onMenuClick = async info => {
    info.domEvent.stopPropagation();

    const { key } = info;

    if (key === 'accept' || key === 'decline') {
      if (loading) return;
      setLoading(true);

      try {
        const action = key === 'accept' ? 'accept' : 'decline';

        await axiosService.post({
          url: `/v1/subscriptions/${record?._id}/invites/${action}`,
          headers: { 'x-resultasobject': true },
        });

        await authService.signInRefresh();
      } catch (err) {
        console.error('[TileItem] Error processing invitation:', err);
        appService.message({
          flag: 'error',
          text: 'error_processing_invitations',
        });
      } finally {
        setLoading(false);
      }

      return;
    }

    console.log('tile.menu.click', { action: key, subscriptionId: record?._id });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // status===1 (invited): both enabled
  // status===2 (accepted/active): only "Revogar Convite" shown
  // status===-1 (declined) | 3 (expired) | other: both disabled
  const status = record?.status;
  const isInvited = status === 1;
  const isActive = status === 2;

  const menuItems = isActive
    ? [{ key: 'decline', icon: <CloseOutlined />, label: 'Revogar Convite' }]
    : [
        { key: 'accept', icon: <LikeOutlined />, label: 'Aceitar Convite', disabled: !isInvited },
        { key: 'decline', icon: <CloseOutlined />, label: 'Recusar Convite', disabled: !isInvited },
        // { key: 'block', icon: <StopOutlined />, label: 'Bloquear Convite' },
        // { type: 'divider' },
        // { key: 'transfer', icon: <SwapOutlined />, label: 'Transferir Organização/Assinatura...' },
      ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────

  const canHover = hoverable && isOwner;

  return (
    <Card
      className={strongBorder ? 'strong-border' : ''}
      onClick={onCardClick}
      style={{
        cursor: isOwner ? 'pointer' : 'default',
        transition: 'box-shadow 0.3s ease',
      }}
      styles={{
        body: {
          background: bgColor,
          transition: 'background 0.3s ease',
          borderRadius: 10,
        },
      }}
      onMouseEnter={
        canHover
          ? e => {
              const card = e.currentTarget;
              const body = card.querySelector('.ant-card-body');
              if (body) body.style.background = hoverBg;
              card.style.boxShadow = hoverShadow;
            }
          : undefined
      }
      onMouseLeave={
        canHover
          ? e => {
              const card = e.currentTarget;
              const body = card.querySelector('.ant-card-body');
              if (body) body.style.background = bgColor;
              card.style.boxShadow = 'none';
            }
          : undefined
      }
    >
      <Flex gap={12} align="start">
        {showAvatar ? (
          <OrganizationAvatar
            organization={record?.organization}
            logo={logo}
            backColor={record?.backColor}
            foreColor={record?.foreColor}
          />
        ) : null}

        <ItemHeader title={record?.organization} profile={record?.profile} />

        {!isOwner && (
          <Dropdown
            trigger={['click']}
            disabled={loading}
            menu={{ items: menuItems, onClick: onMenuClick }}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              icon={loading ? <Spin size="small" /> : <MoreOutlined />}
              onClick={e => e.stopPropagation()}
              style={{ marginLeft: 'auto' }}
              disabled={loading}
            />
          </Dropdown>
        )}
      </Flex>

      <Divider style={{ margin: '12px 0', borderColor: token.colorBorderSecondary }} />

      <Flex justify="space-between" align="center">
        <div>
          <ItemMember team={record.team} />
        </div>
        <Flex gap={8} align="center">
          {!isOwner && <StatusTag status={record?.status} />}
          {flagSrc && (
            <Tooltip title={translateX('origin')}>
              <img
                src={flagSrc}
                alt={countryCode}
                style={FLAG_STYLE}
                onError={e => {
                  e.target.style.display = 'none';
                }}
              />
            </Tooltip>
          )}
        </Flex>
      </Flex>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default TileItem;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function getTeamAvatarSrc(member) {
  const avatar = member?.avatar;
  return avatar?.base64Thumb || avatar?.thumbUrl || avatar?.storageUrl || member?.img;
}
